import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AdminSelectors } from "src/redux/Admin/selectors";
import { AdminActions } from "src/redux/Admin/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";
import Button from "src/components/common/Button";
import Toggle from "src/components/common/Toggle";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";
import { formatKst } from "src/utils/formatKst";
import styles from "./styles.module.scss";

const FeatureFlags = () => {
    const dispatch = useDispatch();
    const flags = useSelector(AdminSelectors.FeatureFlags);
    const flagsLoading = useSelector(AdminSelectors.FlagsLoading);
    const flagsError = useSelector(AdminSelectors.FlagsError);

    const [expandedFlag, setExpandedFlag] = useState(null);
    const [search, setSearch] = useState("");
    // Toggles accumulate here (flag hash -> desired state) and apply only on
    // Confirm Changes - same pending-changes model as the Counterparties page.
    const [pendingToggles, setPendingToggles] = useState(new Map());
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        dispatch(AdminActions.FetchFeatureFlags());
    }, [dispatch]);

    const filteredFlags = useMemo(() => {
        if (!flags) return [];
        const sorted = [...flags].sort((a, b) => a.key.localeCompare(b.key));
        if (!search.trim()) return sorted;
        const q = search.toLowerCase();
        return sorted.filter(f =>
            f.key.toLowerCase().includes(q) ||
            f.description.toLowerCase().includes(q)
        );
    }, [flags, search]);

    const effectiveEnabled = (flag) =>
        pendingToggles.has(flag.hash) ? pendingToggles.get(flag.hash) : flag.enabled;

    const handleToggle = useCallback((flag) => {
        setPendingToggles((prev) => {
            const next = new Map(prev);
            if (next.has(flag.hash)) next.delete(flag.hash); // back to original
            else next.set(flag.hash, !flag.enabled);
            return next;
        });
    }, []);

    const handleDiscard = () => setPendingToggles(new Map());

    const handleConfirm = () => {
        const changes = [...pendingToggles.entries()].map(([hash, enabled]) => {
            const flag = flags.find((f) => f.hash === hash);
            return { hash, key: flag?.key || hash, enabled };
        });
        dispatch(ConfirmActions.Open({
            title: "Apply Flag Changes",
            message: changes
                .map((c) => `${c.key}: ${c.enabled ? "ON" : "OFF"}`)
                .join("\n"),
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for these flag changes",
            okTitle: "Apply",
            cancelTitle: "Cancel",
            warning: true,
            onOk: async (reason) => {
                setApplying(true);
                try {
                    for (const c of changes) {
                        await dispatch(AdminActions.ToggleFlag(c.hash, c.key, c.enabled, reason));
                    }
                    setPendingToggles(new Map());
                } finally {
                    setApplying(false);
                }
            },
        }));
    };

    // Leaving the page with pending toggles asks for confirmation first.
    const pendingSummary = [...pendingToggles.entries()]
        .map(([hash, enabled]) => {
            const flag = flags?.find((f) => f.hash === hash);
            return `${flag?.key || hash}: ${enabled ? "ON" : "OFF"}`;
        })
        .join("\n");
    useUnsavedChangesGuard(pendingToggles.size > 0, pendingSummary);

    // Event timestamps are epoch SECONDS (the codebase convention). formatKst
    // handles the seconds->ms scaling and renders YYYY-MM-DD hh:mm:ss KST.
    // Counterparty overrides only. Group overrides exist but are a test mechanism:
    // the e2e runner sets one per test, so surfacing them here would present
    // hundreds of throwaway groups as operator-relevant configuration.
    const cptyOverrideCount = (flag) =>
        flag.cptyOverrides ? Object.keys(flag.cptyOverrides).length : 0;

    const formatTime = (ts) => formatKst(ts);

    if (flagsLoading && !flags?.length) {
        return (
            <div className={styles.featureFlags}>
                <div className={styles.header}>
                    <div className={styles.title}>Feature Flags</div>
                </div>
                <div className={styles.loading}>Loading flags...</div>
            </div>
        );
    }

    return (
        <div className={`${styles.featureFlags} ${pendingToggles.size > 0 ? styles.hasStatusBar : ""}`}>
            <div className={styles.header}>
                <div className={styles.title}>Feature Flags</div>
                <div className={styles.searchArea}>
                    <input
                        className={styles.searchInput}
                        type="text"
                        placeholder="Search flags..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="flag-search"
                    />
                </div>
            </div>

            {flagsError && (
                <div className={styles.error}>{flagsError}</div>
            )}

            <div className={styles.flagList}>
                <div className={styles.flagHeader}>
                    <div className={styles.flagName}>Flag</div>
                    <div className={styles.flagState}>State</div>
                    <div className={styles.flagOverrides}>Overrides</div>
                    <div className={styles.flagUpdated}>Modified Date</div>
                    <div className={styles.flagActor}>Modified By</div>
                </div>

                {filteredFlags.map((flag) => (
                    <div key={flag.hash} className={styles.flagRow}>
                        <div
                            className={styles.flagSummary}
                            onClick={() => setExpandedFlag(
                                expandedFlag === flag.hash ? null : flag.hash
                            )}
                        >
                            <div className={styles.flagName}>
                                <div className={styles.flagKey}>{flag.key}</div>
                                <div className={styles.flagDesc}>{flag.description}</div>
                            </div>
                            {/* The whole cell (full row height) toggles the
                                flag; mouse clicks land here (the Toggle inside
                                is mouse-transparent, keyboard still reaches its
                                input), and never expand/collapse the row. */}
                            <div
                                className={styles.flagState}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle(flag);
                                }}
                                data-testid={`flag-toggle-cell-${flag.key}`}
                            >
                                <span className={styles.toggleVisual}>
                                    <Toggle
                                        value={effectiveEnabled(flag)}
                                        onChange={() => handleToggle(flag)}
                                        reverseLabel={true}
                                        dataTestId={`flag-toggle-${flag.key}`}
                                        ariaLabel={`Toggle ${flag.key}`}
                                    />
                                </span>
                            </div>
                            <div className={styles.flagOverrides}>
                                {cptyOverrideCount(flag) > 0 ? (
                                    <span className={styles.overrideBadge}>
                                        {cptyOverrideCount(flag)} cpty{cptyOverrideCount(flag) > 1 ? "s" : ""}
                                    </span>
                                ) : (
                                    <span className={styles.noOverrides}>-</span>
                                )}
                            </div>
                            <div className={styles.flagUpdated}>
                                {formatTime(flag.updatedAt)}
                            </div>
                            <div className={styles.flagActor}>
                                {flag.updatedBy || "-"}
                            </div>
                        </div>

                        {expandedFlag === flag.hash && (
                            <FlagDetail flag={flag} />
                        )}
                    </div>
                ))}

                {filteredFlags.length === 0 && !flagsLoading && (
                    <div className={styles.empty}>
                        {search ? "No flags match your search." : "No flags found."}
                    </div>
                )}
            </div>

            {/* Status bar for pending toggles - same fixed bottom bar, text
                shape and button styling as the Counterparties page */}
            {pendingToggles.size > 0 && (
                <div className={styles.statusBar}>
                    <span>{pendingToggles.size} change{pendingToggles.size !== 1 ? "s" : ""} ready to apply</span>
                    <Button
                        value="Discard Changes"
                        onClick={handleDiscard}
                        disabled={applying}
                        classes={[styles.detailBtn]}
                    />
                    <Button
                        value={applying ? "Applying..." : "Confirm Changes"}
                        onClick={handleConfirm}
                        disabled={applying}
                        classes={[styles.removeBtn]}
                    />
                </div>
            )}
        </div>
    );
};

const FlagDetail = ({ flag }) => {
    const dispatch = useDispatch();
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Counterparty overrides only. A group override is how the e2e runner isolates a
    // single test, so those are deliberately not shown here: they are not
    // configuration an operator set, nor anything they should act on.
    const cptyOverrides = flag.cptyOverrides || {};
    const hasCptyOverrides = Object.keys(cptyOverrides).length > 0;

    // Every cpty, so an operator can set an override without knowing codes by heart.
    // The list is already in redux for the Counterparties page; fetch only if the
    // operator opened a flag before visiting that page.
    const cpties = useSelector(AdminSelectors.Cpties);
    const [cptySearch, setCptySearch] = useState("");
    useEffect(() => {
        if (!cpties?.length) dispatch(AdminActions.FetchCpties());
    }, [dispatch, cpties]);

    const candidates = useMemo(() => {
        const all = [...(cpties || [])]
            .map((c) => c.cptyCode)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        const q = cptySearch.trim().toLowerCase();
        return q ? all.filter((code) => code.toLowerCase().includes(q)) : all;
    }, [cpties, cptySearch]);

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const states = await dispatch(AdminActions.FetchFlagHistory(flag.hash));
            // The endpoint returns the flag's state after each event,
            // oldest first; show newest first.
            setHistory([...(states || [])].reverse());
        } catch (e) {
            console.error("Failed to load flag history:", e);
        } finally {
            setHistoryLoading(false);
        }
    }, [dispatch, flag.hash]);

    // Expanding a flag loads its history immediately (no extra click).
    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleSetOverride = (cptyCode, enabled) => {
        const current = cptyOverrides[cptyCode];
        const verb = current === undefined ? "Set" : "Change";
        dispatch(ConfirmActions.Open({
            title: `${verb} Override`,
            message: `${verb} the counterparty override for ${cptyCode} on ${flag.key} `
                + `to ${enabled ? "ON" : "OFF"}?\n`
                + `This applies to ${cptyCode} only; every other counterparty keeps the `
                + `flag's global state.`,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: `Reason for this override on ${cptyCode}`,
            okTitle: verb,
            cancelTitle: "Cancel",
            onOk: async () => {
                try {
                    await dispatch(AdminActions.SetFlagOverride(
                        flag.hash, flag.key, "CPTY", cptyCode, enabled));
                    dispatch(AdminActions.FetchFeatureFlags());
                } catch (e) {
                    console.error("Failed to set override:", e);
                }
            },
        }));
    };

    const handleRemoveOverride = (scopeValue) => {
        dispatch(ConfirmActions.Open({
            title: "Remove Override",
            message: `Remove the counterparty override for ${scopeValue} on ${flag.key}?\n`
                + `${scopeValue} falls back to the flag's global state.`,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for removing this override",
            okTitle: "Remove",
            cancelTitle: "Cancel",
            warning: true,
            onOk: async () => {
                try {
                    await dispatch(AdminActions.RemoveFlagOverride(
                        flag.hash, flag.key, "CPTY", scopeValue));
                    dispatch(AdminActions.FetchFeatureFlags());
                } catch (e) {
                    console.error("Failed to remove override:", e);
                }
            },
        }));
    };

    return (
        <div className={styles.flagDetail}>
            <div className={styles.detailSection}>
                <div className={styles.detailLabel}
                     data-testid={`flag-cpty-overrides-${flag.key}`}>
                    Counterparty overrides
                </div>
                {hasCptyOverrides ? (
                    <div className={styles.overrideList}>
                        {Object.entries(cptyOverrides).map(([cpty, enabled]) => (
                            <div key={cpty} className={styles.overrideRow}>
                                <span className={styles.overrideScope}>cpty</span>
                                <span className={styles.overrideName}>{cpty}</span>
                                <span className={enabled ? styles.overrideOn : styles.overrideOff}>
                                    {enabled ? "ON" : "OFF"}
                                </span>
                                <button
                                    className={styles.overrideRemoveBtn}
                                    onClick={() => handleRemoveOverride(cpty)}
                                    data-testid={`remove-override-${flag.key}-${cpty}`}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.detailValue}>None</div>
                )}
            </div>

            <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Set a counterparty override</div>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search counterparties..."
                    value={cptySearch}
                    onChange={(e) => setCptySearch(e.target.value)}
                    data-testid={`cpty-override-search-${flag.key}`}
                />
                <div className={styles.overrideList}>
                    {candidates.length === 0 ? (
                        <div className={styles.detailValue}>
                            {cpties?.length ? "No counterparty matches that search" : "Loading..."}
                        </div>
                    ) : (
                        candidates.map((code) => {
                            const current = cptyOverrides[code];
                            return (
                                <div key={code} className={styles.overrideRow}>
                                    <span className={styles.overrideScope}>cpty</span>
                                    <span className={styles.overrideName}>{code}</span>
                                    <span className={
                                        current === undefined
                                            ? styles.noOverrides
                                            : current ? styles.overrideOn : styles.overrideOff
                                    }>
                                        {current === undefined
                                            ? `global (${flag.enabled ? "ON" : "OFF"})`
                                            : current ? "ON" : "OFF"}
                                    </span>
                                    <button
                                        className={styles.overrideRemoveBtn}
                                        disabled={current === true}
                                        onClick={() => handleSetOverride(code, true)}
                                        data-testid={`set-override-on-${flag.key}-${code}`}
                                    >
                                        ON
                                    </button>
                                    <button
                                        className={styles.overrideRemoveBtn}
                                        disabled={current === false}
                                        onClick={() => handleSetOverride(code, false)}
                                        data-testid={`set-override-off-${flag.key}-${code}`}
                                    >
                                        OFF
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className={styles.detailSection}>
                <div className={styles.detailLabel} data-testid={`flag-history-${flag.key}`}>
                    History{historyLoading ? " (loading...)" : ""}
                </div>
                {history && (
                    <div className={styles.historyList}>
                        {history.length > 0 && (
                            <div className={`${styles.historyRow} ${styles.historyHeader}`}>
                                <span className={styles.historyType}>State</span>
                                <span className={styles.historyUser}>Modified By</span>
                                <span className={styles.historyTime}>Modified Date</span>
                            </div>
                        )}
                        {history.map((state, i) => (
                            <div key={i} className={styles.historyRow}>
                                <span className={styles.historyType}>
                                    {state.deleted ? "Deleted" : state.enabled ? "Enabled" : "Disabled"}
                                </span>
                                <span className={styles.historyUser}>
                                    {state.updatedBy || "-"}
                                </span>
                                <span className={styles.historyTime}>
                                    {state.updatedAt ? formatKst(state.updatedAt) : "-"}
                                </span>
                            </div>
                        ))}
                        {history.length === 0 && (
                            <div className={styles.detailValue}>No history found.</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureFlags;
